import CryptoJS from "crypto-js";
import PatienceDiff from "@/utils/PatienceDiff";

/**
 * Internal utility functions
 */
export default {
  getJWTPayload(jwtToken) {
    const base64Url = jwtToken.split(".")[1];
    return JSON.parse(window.atob(base64Url));
  },
  /**
   * Format the exercises answer strings (user input & final answer) :
   * - replace ' to have the same
   * - remove punctuation
   * - remove extra whitespaces
   * - set to lowercase if case sensitive
   *
   * @param {String} str
   * @param {Boolean} case_sensitive
   * @returns Sanitized string
   */
  sanitizeExerciseAnswer(str, case_sensitive = true) {
    if (str !== undefined && str.length > 0) {
      // Make sure the same apostrophe is used
      let res = str.replaceAll("’", "'");
      // Remove all punctuation (hyphen not removed)
      res = res.replaceAll(/[.,/#?!$%^&*;:{}=\\_`~()]/g, "");
      // Remove extra spaces
      res = res.replaceAll(/\s+/g, " ").trim();
      // Remove spaces around hyphen to ignore them in the correction
      res = res.replace(/\s*-\s*/g, "-");

      if (case_sensitive === false) {
        res = res.toLowerCase();
      }

      return res;
    } else {
      return "";
    }
  },
  /**
   * Same sanitizing rules as @sanitizeExerciseAnswer,
   * except the changes are made char by char.
   *
   * For each change that occured,
   * save the change at the corresponding index in the sanitized string.
   *
   * @param {String} str
   * @param {Boolean} is_case_sensitive
   * @returns Object with sanitized string and details of the changes
   */
  sanitizeCharByChar(str, is_case_sensitive = true) {
    let res = "";
    let changed = new Map(); // characters changed at index in sanitized string
    let removed = new Map(); // chars removed at index in sanitized string
    let isPunctuation = new RegExp("[.,/#?!$%^&*;:{}=\\_`~()]");

    let char = "";
    let previousChar = ""; // previous char in the res string
    let resIndex = -1;
    for (var i = 0; i < str.length; i++) {
      char = str.charAt(i);
      resIndex = res.length;

      if (!is_case_sensitive && char.toLowerCase() !== char) {
        // lower case: save char changed
        res += char.toLowerCase();
        changed.set(resIndex, char);
        previousChar = char;
      } else if (
        isPunctuation.test(char) ||
        (char === " " && previousChar === " ")
      ) {
        // punctuation or extra whitespace removed, save info
        if (!removed.has(resIndex)) {
          removed.set(resIndex, "");
        }

        removed.set(resIndex, removed.get(resIndex) + char);
      } else {
        res += char;
        previousChar = char;
      }
    }

    res = res.replaceAll("’", "'"); // don't record that change

    return {
      str: res,
      changed: changed,
      removed: removed,
    };
  },
  adjustVerticalLineBottomOffset(exercise, el, boxOnMidLine = false) {
    try {
      // Adjust vertical line so it doesn't overflow under the last box
      const resizeObserver = new ResizeObserver((entries) => {
        try {
          for (let entry of entries) {
            const exercisePartParent = entry.target.closest(".exercise-part");
            let verticalLine =
              exercisePartParent.querySelectorAll(".vertical-line")[0];

            let targetHeightAjustment = entry.target.clientHeight;
            if (boxOnMidLine === true) {
              targetHeightAjustment = parseInt(targetHeightAjustment / 2);
            }
            verticalLine.style.bottom = `${targetHeightAjustment}px`;
          }
        } catch (error) {
          console.log(
            "error adjusting vertical line (might be expected)",
            error,
          );
        }
      });

      // Observe the last question node of each exercise part
      for (const exercisePart of exercise.exercise_parts) {
        const exercisePartQuestions = el.querySelectorAll(
          `#exercise-part-${exercisePart.id} .question-line-container`,
        );

        // start observing a DOM node
        resizeObserver.observe(
          exercisePartQuestions[exercisePartQuestions.length - 1],
        );
      }
    } catch (error) {
      console.log("error setting up the vertical line observer", error);
    }
  },
  /**
   * Given an array of parts of a fill-in-the-blanks question,
   * loop through each part and for each containing a separator,
   * split the part until the separators are the unique character in their part.
   *
   * @param {array} parts parts of a fill-in-the-blanks question
   * @returns Array : same parts where those containing a separator have been splitted.
   * Ex: ["I", "am", "…,", "now"] => ["I", "am", "…", ",", "now"]
   */
  splitSeparatorParts(parts) {
    let partsCopy = null;

    for (let i = 0; i < parts.length; i++) {
      // If the part includes the separator
      if (parts[i].includes("…")) {
        // Split each character
        const separatorParts = parts[i].split("");

        // If the separator is not alone in its part
        if (separatorParts.length > 1) {
          // Hard copy of the param
          partsCopy = [...parts];

          // Insert in the parts array, instead of the part containing the separator, each character of the part.
          partsCopy.splice(i, 1, ...separatorParts);

          // parts length has changed, break loop and recall the function
          break;
        }
      }
    }

    // partsCopy is not null, a part was splitted, recall the function...
    if (partsCopy !== null) {
      return this.splitSeparatorParts(partsCopy);
    } else {
      // ... until no part has been splitted.
      return parts;
    }
  },
  /**
   * Given an array of parts take care of adding class for italic
   * and underlined words
   * @param {array} parts parts of a fill-in-the-blanks question
   * @returns Array : same parts where those containing a separator have been splitted.
   * Ex: ["I", "*am*", "…", "now"] => ["I", "<span class="italic">am</span>", "…", ",", "now"]
   */
  formatParts(parts) {
    let partsFinal = [];
    let italic = 0;
    let underline = 0;

    for (let part of parts) {
      let textQuestion = part;
      if (part != "…") {
        if (italic % 2 == 1)
          textQuestion = '<span class="instructions-italic">' + textQuestion;
        if (underline % 2 == 1)
          textQuestion =
            '<span class="instructions-underlined">' + textQuestion;

        while (textQuestion != textQuestion.replace("*", " ")) {
          if (textQuestion != textQuestion.replace("*", " ")) italic++;
          if (italic % 2)
            textQuestion = textQuestion.replace(
              "*",
              '<span class="instructions-italic">',
            );
          else textQuestion = textQuestion.replace("*", "</span>");
        }
        while (textQuestion != textQuestion.replace("_", " ")) {
          if (textQuestion != textQuestion.replace("_", " ")) underline++;
          if (underline % 2)
            textQuestion = textQuestion.replace(
              "_",
              '<span class="instructions-underlined">',
            );
          else textQuestion = textQuestion.replace("_", "</span>");
        }
        if (italic % 2 == 1) textQuestion = textQuestion + "</span>";
        if (underline % 2 == 1) textQuestion = textQuestion + "</span>";
      }
      partsFinal.push(textQuestion);
    }
    return partsFinal;
  },
  /**
   * Given a text take care of adding class for italic
   * and underlined words
   * @param text text of a question
   * @returns Array : same parts where those containing a separator have been splitted.
   * Ex: "the *house is* blue" => "the <span class="italic">house is</span> blue"
   */
  formatText(text) {
    let textQuestion = text;

    let italic = 0;
    let underline = 0;
    while (textQuestion != textQuestion.replace("*", " ")) {
      if (textQuestion != textQuestion.replace("*", " ")) italic++;
      if (italic % 2)
        textQuestion = textQuestion.replace(
          "*",
          '&nbsp;<span class="instructions-italic">',
        );
      else textQuestion = textQuestion.replace("*", "</span>&nbsp;");
    }
    while (textQuestion != textQuestion.replace("_", " ")) {
      if (textQuestion != textQuestion.replace("_", " ")) underline++;
      if (underline % 2)
        textQuestion = textQuestion.replace(
          "_",
          '&nbsp;<span class="instructions-underlined">',
        );
      else textQuestion = textQuestion.replace("_", "</span>&nbsp;");
    }

    if (textQuestion.startsWith("&nbsp;"))
      textQuestion = textQuestion.substring(6);

    while (textQuestion != textQuestion.replace("&nbsp;&nbsp;", " ")) {
      textQuestion = textQuestion.replace("&nbsp;&nbsp;", "&nbsp;");
    }
    return textQuestion;
  },
  /**
   * Look if the student has saved data for a given question
   * @param {Array} studentAnswers array of student answers
   * @param {Int} questionId
   * @returns Array of user inputs, empty if none saved
   */
  questionUserInputs(studentAnswers, questionId) {
    const questionStudentAnswers = studentAnswers.find(
      (element) => element.question_id == questionId,
    );

    return questionStudentAnswers === undefined
      ? []
      : questionStudentAnswers.user_inputs;
  },
  /**
   * Clear all the inputs contained in the parameter element
   * @param {*} el DOM element
   */
  resetInputs(el) {
    const el_inputs = el.querySelectorAll(`input`);
    for (let input of el_inputs) {
      input.value = "";
    }
  },
  /**
   * function that stores the value of new cookie variable
   * @param {String} cname cookie name
   * @param {String} cvalue cookie value
   * @param {Int} extime cookie expiration time
   * @param {String} extype days | hours | minutes. Cookie expiration duration type. Default: days.
   */
  setCookie(cname, cvalue, extime, extype = "days") {
    const d = new Date();
    let extDuration = 0;
    if (extype === "days") extDuration = extime * 24 * 60 * 60 * 1000;
    else if (extype === "hours") extDuration = extime * 60 * 60 * 1000;
    else if (extype === "minutes") extDuration = extime * 60 * 1000;
    else throw new Error("Invalid value for parameter extype.");
    d.setTime(d.getTime() + extDuration);
    let expires = "expires=" + d.toUTCString();

    // Warning: with SameSite=Lax, cookies won't be set in iFrames
    document.cookie =
      cname + "=" + cvalue + ";" + expires + ";path=/;secure;SameSite=Lax";
  },
  /**
   * function that return the value of cookie variable if defined, or else return undefined
   * @param {String} cname cookie name
   * @returns cookie value
   */
  getCookie(cname) {
    let name = cname + "=";
    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == " ") {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return undefined;
  },
  /**
   * function that deletes a cookie
   * @param {String} cname cookie name
   */
  deleteCookie(cname) {
    const d = new Date();
    d.setTime(d.getTime() - 2 * 24 * 60 * 60 * 1000);
    let expires = "expires=" + d.toUTCString();
    document.cookie = cname + "=null;" + expires + ";path=/";
  },
  /**
   * Adjust width of input containers to fit the longest answer for all exercise parts.
   * Can also be used directly on the input via a class of the input.
   * Creates a mock div into the document to compare sizes.
   *
   * @param {Object} exercise
   * @param {*} el
   * @param {String} inputParentClassName class of the parent container or the input itself
   * @param {Boolean} withSubparts does the exercise have subparts?
   * @param {Boolean} largeInputs Bigger min & max values for large inputs
   * @param {String} classToReset Class for which we'll reset maxWidth before adjusting, if set
   * @param {Boolean} noMax If true, don't limit maxWidth
   */
  adjustInputsContainersWidth(
    exercise,
    el,
    inputParentClassName,
    withSubparts = false,
    largeInputs = false,
    classToReset = null,
    noMax = false,
  ) {
    if (exercise.exercise_parts[0] === undefined) {
      return;
    }
    // Reset if needed
    if (classToReset !== null) {
      let inputPartsToReset = el.querySelectorAll(`.${classToReset}`);
      for (let item of inputPartsToReset) {
        item.style.removeProperty("width");
        item.style.removeProperty("max-width");
      }
    }

    if (largeInputs === true && window.screen.width <= 768) {
      // Don't adjust size for large inputs on small screens
      return;
    }

    let answers = [];

    // Iterate over all exercise parts
    for (const exercisePart of exercise.exercise_parts) {
      if (withSubparts === true) {
        exercisePart.subparts.forEach((subpart) => {
          subpart.questions.forEach((question) => {
            question.answers.forEach((answer) => {
              answers.push(answer.content);
            });
          });
        });
      } else {
        exercisePart.questions.forEach((question) => {
          question.answers.forEach((answer) => {
            answers.push(answer.content);
          });
        });
      }
    }

    let div = document.createElement("div");
    div.classList.add("mock-div");
    document.getElementById("app").appendChild(div);

    let maxWidth = 0;
    for (let answer of answers) {
      div.innerHTML = "";

      let newContent = document.createTextNode(answer);
      div.appendChild(newContent);

      if (div.offsetWidth > maxWidth) {
        maxWidth = div.offsetWidth;
      }
    }

    // Inputs padding
    maxWidth += 40;
    // Potential help item
    maxWidth += 20;

    if (largeInputs === false) {
      // Min width is 100px (+20px for help item) ; max is 260px for responsive purpose
      maxWidth = maxWidth < 120 ? 120 : maxWidth;
      maxWidth = maxWidth > 260 && !noMax ? 260 : maxWidth;
    } else {
      maxWidth = maxWidth < 200 ? 200 : maxWidth;
      maxWidth = maxWidth > 280 && !noMax ? 280 : maxWidth;
    }

    let inputParts = el.querySelectorAll(`.${inputParentClassName}`);
    for (let item of inputParts) {
      item.style.width = `${maxWidth}px`;
      item.style.maxWidth = `${maxWidth}px`;
    }

    // Remove mock div
    div.parentNode.removeChild(div);
  },
  /**
   * Compares a user answer to it's correction(s).
   *
   * Compare the input to all correct answers using the PatienceDiff algorithm.
   * Get a score for each diff: represents a "distance" between the input & the answer.
   * Build a correction string using the closest answer to user input.
   *
   * In the correction, we mark the whole word incorrect if we find an error.
   * This allows to cover both cases when a char is too much or missing in a word.
   *
   * Restore the exact user input by "reverse sanitizing",
   * by keeping track of the changes that occured during sanitizing.
   *
   * @param {String} userAnswer raw user input
   * @param {Array} correctAnswers array of all possible corrections
   * @param {Boolean} is_case_sensitive should the correction be case sensitive
   * @returns String : HTML text where the wrong parts are separated by a <span>
   */
  correctedUserResponse(userAnswer, correctAnswers, is_case_sensitive) {
    if (!userAnswer) {
      return "";
    }

    let sanitizeDetails = this.sanitizeCharByChar(
      userAnswer,
      is_case_sensitive,
    );
    let sanitizedUserAnswer = sanitizeDetails.str;

    let diffs = [];
    let closestDiffIndex = null;

    for (let i = 0; i < correctAnswers.length; i++) {
      // Get differences between correct answer & user answer, using PatienceDiff algorithm
      let diff = PatienceDiff.patienceDiff(
        correctAnswers[i].split(""),
        sanitizedUserAnswer.split(""),
      );

      // Get the diff score of each correction: each difference between the strings adds a point
      diff.diffScore = diff.lineCountDeleted + diff.lineCountInserted;

      diffs.push(diff);

      // Store index of the smallest diff (ie smallest score)
      if (
        closestDiffIndex === null ||
        diffs[i].diffScore < diffs[closestDiffIndex].diffScore
      ) {
        closestDiffIndex = i;
      }
    }

    /* Build a correction string */

    // Compare input with the closest good answer
    let diff = diffs[closestDiffIndex];

    // This allows the following algorithm to consider the final word
    diff.lines.push({
      line: " ",
      aIndex: -1,
      bIndex: 1,
    });

    let res = "";
    let word = {
      str: "",
      hasError: false,
    };
    let lastWordOk = true;

    // For end-of-word detection
    let sliceIndex = -1;
    let removedChar = "";
    let isAlphanum = new RegExp("[a-zA-Z0-9]");

    diff.lines.forEach((charDiff, key) => {
      // Proceed word by word in user input
      if (
        charDiff.line !== " " ||
        (charDiff.line === " " && charDiff.bIndex === -1)
      ) {
        // Add removed chars by sanitizing
        if (sanitizeDetails.removed.has(charDiff.bIndex)) {
          word.str += sanitizeDetails.removed.get(charDiff.bIndex);
          sanitizeDetails.removed.delete(charDiff.bIndex);
        }

        // char present in user input, put it in correction
        if (charDiff.bIndex > -1) {
          // Restore changed char by sanitizing
          if (sanitizeDetails.changed.has(charDiff.bIndex)) {
            word.str += sanitizeDetails.changed.get(charDiff.bIndex);
          } else {
            word.str += charDiff.line;
          }
        }

        if (charDiff.aIndex == -1 || charDiff.bIndex == -1) {
          word.hasError = true;
        }
      } else {
        // empty str = no char in this word in user input = missing word = don't put in correction
        if (word.str != "") {
          if (lastWordOk && word.hasError) {
            // If last word was ok and new word has error, start html error tag
            res += `<span class="err-word">`;

            lastWordOk = false; // last word is nok until a new word is ok
          } else if (!lastWordOk && !word.hasError) {
            // If last word had error and new word doesn't, close error tag
            // Find the first alphanumeric character from the end of the string
            sliceIndex = -1;
            while (
              !isAlphanum.test(res.slice(0, sliceIndex).slice(-1)) &&
              sliceIndex >= -res.length
            ) {
              sliceIndex -= 1;
            }

            // Put end error tag right after the last word (alphanumeric) in the string
            removedChar = res.slice(sliceIndex);
            res = res.slice(0, sliceIndex);
            res += `</span>` + removedChar;

            lastWordOk = true;
          }

          // Add word to res str
          res += word.str;

          // Add removed chars by sanitizing
          if (sanitizeDetails.removed.has(charDiff.bIndex)) {
            res += sanitizeDetails.removed.get(charDiff.bIndex);
            sanitizeDetails.removed.delete(charDiff.bIndex);
          }

          if (key !== diff.lines.length - 1) {
            res += " ";
          }
        }

        // reset word var
        word = {
          str: "",
          hasError: false,
        };
      }
    });

    // End error tag if needed
    if (!lastWordOk) {
      res += `</span>`;
    }

    // Add char removed at the end of the string
    if (sanitizeDetails.removed.has(sanitizedUserAnswer.length)) {
      res += sanitizeDetails.removed.get(sanitizedUserAnswer.length);
    }

    return res;
  },
  CSVToArray(str, delimiter = ";") {
    // Parse the whole file char by char, RFC 4180 style, so that a
    // delimiter or newline wrapped in double quotes ("...") is treated
    // as literal field content instead of a separator, and "" inside a
    // quoted field is an escaped literal quote.
    const rows = [];
    let row = [];
    let field = "";
    let inQuotes = false;

    for (let i = 0; i < str.length; i++) {
      const char = str[i];

      if (inQuotes) {
        if (char === '"') {
          if (str[i + 1] === '"') {
            field += '"';
            i++;
          } else {
            inQuotes = false;
          }
        } else {
          field += char;
        }
        continue;
      }

      if (char === '"' && field === "") {
        // only a quote at the very start of a field opens a quoted
        // field (RFC 4180) — a quote elsewhere (e.g. an inches mark
        // like 1h30") is kept as a literal character
        inQuotes = true;
      } else if (char === delimiter) {
        row.push(field);
        field = "";
      } else if (char === "\r") {
        continue;
      } else if (char === "\n") {
        row.push(field);
        rows.push(row);
        row = [];
        field = "";
      } else {
        field += char;
      }
    }
    if (field !== "" || row.length > 0) {
      row.push(field);
      rows.push(row);
    }

    // drop fully empty trailing rows (e.g. trailing newline at EOF)
    const nonEmptyRows = rows.filter((r) => !(r.length === 1 && r[0] === ""));

    const headers = (nonEmptyRows.shift() || []).map((elt) => elt.trim());

    // Map the rows
    // use headers.reduce to create an object
    // object properties derived from headers:values
    // the object passed as an element of the array
    const arr = nonEmptyRows.map(function (values) {
      const el = headers.reduce(function (object, header, index) {
        object[header] = values[index];
        return object;
      }, {});
      return el;
    });

    // return the array
    return arr;
  },
  getBrowserInformation() {
    var ua = navigator.userAgent;
    var tem;
    var M =
      ua.match(
        /(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i,
      ) || [];
    if (/trident/i.test(M[1])) {
      tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
      return "IE " + (tem[1] || "");
    }
    if (M[1] === "Chrome") {
      tem = ua.match(/\b(OPR|Edge)\/(\d+)/);
      if (tem != null) return tem.slice(1).join(" ").replace("OPR", "Opera");
    }
    M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, "-?"];
    if ((tem = ua.match(/version\/(\d+)/i)) != null) M.splice(1, 1, tem[1]);
    return M.join(" ");
  },
  download(filename, text) {
    var element = document.createElement("a");
    element.setAttribute(
      "href",
      "data:text/plain;charset=utf-8," + encodeURIComponent(text),
    );
    element.setAttribute("download", filename);
    element.style.display = "none";
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);
  },
  concatenateExercisesMetaForSearch(data) {
    const exercisesMetaGrammar = data.grammar;
    const exercisesMetaVocabulary = data.vocabulary;
    const exercisesMetaListening = data.listening;
    const exercisesMetaSpeaking = data.speaking;
    const exercisesMetaCulture = data.culture;

    let exercises = [];
    Object.entries(exercisesMetaGrammar.chapters).forEach(
      ([chapterName, chapterObject]) => {
        Object.entries(chapterObject.subchapters).forEach(
          ([subchapterName, subchapterObject]) => {
            const tempExercises = subchapterObject.exercises.map((exercise) => {
              exercise["chapter"] = chapterName;
              exercise["subchapter"] = subchapterName;
              exercise["type"] = "Grammar";

              return exercise;
            });
            exercises = exercises.concat(tempExercises);
          },
        );
      },
    );

    Object.entries(exercisesMetaVocabulary.levels).forEach(
      ([, levelObject]) => {
        Object.entries(levelObject.chapters).forEach(
          ([chapterName, chapterObject]) => {
            Object.entries(chapterObject.subchapters).forEach(
              ([subchapterName, subchapterObject]) => {
                const tempExercises = subchapterObject.exercises.map(
                  (exercise) => {
                    exercise["chapter"] = chapterName;
                    exercise["subchapter"] = subchapterName;
                    exercise["type"] = "Vocabulary";

                    return exercise;
                  },
                );
                exercises = exercises.concat(tempExercises);
              },
            );
          },
        );
      },
    );

    Object.entries(exercisesMetaListening.levels).forEach(([, levelObject]) => {
      Object.entries(levelObject.chapters).forEach(
        ([chapterName, chapterObject]) => {
          Object.entries(chapterObject.subchapters).forEach(
            ([subchapterName, subchapterObject]) => {
              const tempExercises = subchapterObject.exercises.map(
                (exercise) => {
                  exercise["chapter"] = chapterName;
                  exercise["subchapter"] = subchapterName;
                  exercise["type"] = "Listening";

                  return exercise;
                },
              );
              exercises = exercises.concat(tempExercises);
            },
          );
        },
      );
    });

    Object.entries(exercisesMetaSpeaking.levels).forEach(([, levelObject]) => {
      Object.entries(levelObject.chapters).forEach(
        ([chapterName, chapterObject]) => {
          Object.entries(chapterObject.subchapters).forEach(
            ([subchapterName, subchapterObject]) => {
              const tempExercises = subchapterObject.exercises.map(
                (exercise) => {
                  exercise["chapter"] = chapterName;
                  exercise["subchapter"] = subchapterName;
                  exercise["type"] = "Speaking";

                  return exercise;
                },
              );
              exercises = exercises.concat(tempExercises);
            },
          );
        },
      );
    });

    Object.entries(exercisesMetaCulture.levels).forEach(([, levelObject]) => {
      Object.entries(levelObject.chapters).forEach(
        ([chapterName, chapterObject]) => {
          Object.entries(chapterObject.subchapters).forEach(
            ([subchapterName, subchapterObject]) => {
              const tempExercises = subchapterObject.exercises.map(
                (exercise) => {
                  exercise["chapter"] = chapterName;
                  exercise["subchapter"] = subchapterName;
                  exercise["type"] = "Culture";

                  return exercise;
                },
              );
              exercises = exercises.concat(tempExercises);
            },
          );
        },
      );
    });

    return exercises;
  },
  /**
   * Encrypt data using AES encryption.
   *
   * @param {Object} data to encrypt. JSON serializable object.
   * @returns encrypted data String
   */
  aesEncrypt(data) {
    const iv = import.meta.env.VITE_AES_IV;
    const key = import.meta.env.VITE_AES_SECRET_KEY;

    const keyWA = CryptoJS.enc.Utf8.parse(key);
    const ivWA = CryptoJS.enc.Utf8.parse(iv);

    let encryptText = JSON.stringify(data);
    var encrypted = CryptoJS.AES.encrypt(encryptText, keyWA, {
      iv: ivWA,
      mode: CryptoJS.mode.CBC,
    });

    return encrypted.toString();
  },
  /**
   * Decrypt AES encrypted data.
   *
   * @param {String} ciphertext
   * @returns Object decrypted
   */
  aesDecrypt(ciphertext) {
    try {
      if (ciphertext) {
        const iv = import.meta.env.VITE_AES_IV;
        const key = import.meta.env.VITE_AES_SECRET_KEY;

        const keyWA = CryptoJS.enc.Utf8.parse(key);
        const ivWA = CryptoJS.enc.Utf8.parse(iv);

        let bytes = CryptoJS.AES.decrypt(ciphertext, keyWA, {
          iv: ivWA,
          mode: CryptoJS.mode.CBC,
        });
        let decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));

        return decryptedData;
      }
    } catch (error) {
      console.log("cryoptojs decryption error", error);
      return null;
    }
  },
  /**
   * Move element at old_ind to new_index in arr.
   * @param {Array} arr
   * @param {Int} old_index
   * @param {Int} new_index
   */
  array_move(arr, old_index, new_index) {
    while (old_index < 0) {
      old_index += arr.length;
    }
    while (new_index < 0) {
      new_index += arr.length;
    }
    if (new_index >= arr.length) {
      var k = new_index - arr.length + 1;
      while (k--) {
        arr.push(undefined);
      }
    }
    arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
  },
  fisherYatesShuffles(str) {
    let a = str.split("");
    let n = a.length;

    for (let i = n - 1; i > 0; i--) {
      let j = Math.floor(Math.random() * (i + 1));
      let tmp = a[i];
      a[i] = a[j];
      a[j] = tmp;
    }
    return a.join("");
  },
  /**
   * Remove accents from a string.
   * @param {String} str
   * @returns String without accents
   */
  removeAccents(str) {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  },
  /**
   * Very basic password generation.
   *
   * @returns String
   */
  generatePassword() {
    const pswdMinLength = 8;
    const types = {
      letters: "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ",
      numbers: "0123456789",
      symbols: "!@#$%&*()_+?-=",
    };

    let pwd = "";
    for (let i = 0; i < pswdMinLength; i += 3) {
      pwd += types.letters[Math.floor(Math.random() * types.letters.length)];
      pwd += types.numbers[Math.floor(Math.random() * types.numbers.length)];
      pwd += types.symbols[Math.floor(Math.random() * types.symbols.length)];
    }

    return this.fisherYatesShuffles(pwd);
  },
  /**
   * Sometimes when updating large parts of the DOM, Vue.nextTick is not enough.
   * Use DoubleRaf to ensure DOM has been updated and smooth interaction.
   * @param {*} callback
   */
  doubleRaf(callback) {
    requestAnimationFrame(() => {
      requestAnimationFrame(callback);
    });
  },
  /**
   * Focus the first input of a component.
   */
  focusFirstInput(el, className) {
    this.doubleRaf(() => {
      const firstInput = el.querySelector(className);
      if (firstInput) {
        firstInput.focus();
      }
    });
  },
  adjustVerticalLineTopOffset(firstQuestion, verticalLine) {
    this.doubleRaf(() => {
      if (!firstQuestion || !verticalLine) return;

      const firstBoxHeight = firstQuestion.clientHeight;
      const topValue = firstBoxHeight / 2;

      verticalLine.style.top = `${topValue}px`;
    });
  },
};
